import * as _ from 'lodash-es';
import moment from 'moment';
import Embed from 'quill/blots/embed';
import type QuillScroll from 'quill/blots/scroll';
import React from 'react';
import { createPortal } from 'react-dom';
import Pill from '../../components/Pill';
import { useFacetEdit } from '../../topbar/FacetEditContext';
import { useSearchObjects } from '../../topbar/SearchObjects';
import type { SearchFacetValue } from '../../topbar/quillFacets';
import { findFacet } from '../../topbar/quillFacets';
import Scroll from './Scroll';
import * as styles from './SearchFacet.module.scss';

export function formatSearchDate(date: moment.Moment) {
  const isCurrentYear = date.isSame(moment(), 'year');
  const format = isCurrentYear ? 'MMM DD' : 'MMM DD, YYYY';
  return date.format(format);
}

interface SearchFacetMentionProps extends SearchFacetValue {
  selected: boolean;
  blot: Embed;
}

const SearchFacetMention = ({
  prefix,
  content,
  separator,
  selected,
  blot,
}: SearchFacetMentionProps) => {
  const facet = findFacet(prefix, separator);
  const searchObjects = useSearchObjects();

  const display = (() => {
    if (facet && facet.type === 'date') {
      const parsed = moment(content);
      return formatSearchDate(parsed);
    }

    const entities = facet?.entityTypes
      .map((type) => searchObjects[type]?.objects)
      .flat();

    const entity = entities?.find((e) => e.id === content);
    if (entity != null) {
      return 'name' in entity ? entity.name : entity.title;
    }

    const type =
      facet?.type === 'entity' ? (facet.entityTypes[0] ?? 'entity') : 'entity';

    return `(unknown ${type})`;
  })();

  const { onEditFacet } = useFacetEdit();

  return (
    <Pill
      type="facet"
      selected={selected}
      size="small"
      tabIndex={-1}
      onClick={() => onEditFacet({ blot, prefix, separator })}
      className={styles.facetPill}
      noWrap
    >
      {prefix}
      {separator} {display}
    </Pill>
  );
};

class SearchFacet extends Embed {
  static blotName = 'search-facet';
  static className = 'ql-search-facet';
  static tagName = 'SPAN';

  static create(value: SearchFacetValue) {
    const node = super.create(value) as HTMLSpanElement;
    node.setAttribute('data-content', value.content);
    node.setAttribute('data-prefix', value.prefix);
    node.setAttribute('data-separator', value.separator);
    return node;
  }

  static value(domNode: Element): SearchFacetValue {
    return {
      content: domNode.getAttribute('data-content') ?? '',
      prefix: domNode.getAttribute('data-prefix') ?? '',
      separator: domNode.getAttribute('data-separator') ?? '',
    };
  }

  id: string;

  constructor(scroll: QuillScroll, domNode: HTMLElement) {
    super(scroll, domNode);
    this.id = _.uniqueId();
  }

  attach() {
    super.attach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_MOUNT, this);
  }

  detach() {
    super.detach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_UNMOUNT, this);
  }

  renderPortal({ selected }: { selected: boolean }) {
    const props = this.statics.value(this.domNode);

    return createPortal(
      <SearchFacetMention {...props} selected={selected} blot={this} />,
      this.contentNode,
    );
  }
}

export default SearchFacet;
